<template>
  <div
    class="modal fade"
    id="affiliateConfigModal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="affiliateConfigModalLabel"
    aria-hidden="true"
    ref="modal"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content">

        <!-- HEADER -->
        <div class="modal-header bg-gradient-primary">
          <h5 class="modal-title text-white" id="affiliateConfigModalLabel">
            Configurar Programa de Afiliados
          </h5>
          <button type="button" class="btn-close text-white" @click="closeModal" aria-label="Close"></button>
        </div>

        <!-- ALERTA INLINE -->
        <div v-if="alert.show" class="alert mb-0 rounded-0 d-flex align-items-center gap-2"
          :class="alert.type === 'success' ? 'alert-success' : 'alert-danger'"
          style="border-left: 4px solid; border-radius: 0;"
        >
          <i class="material-icons text-sm">{{ alert.type === 'success' ? 'check_circle' : 'error' }}</i>
          <span class="text-sm font-weight-bold">{{ alert.message }}</span>
          <button type="button" class="btn-close ms-auto" style="font-size: 0.7rem;" @click="alert.show = false"></button>
        </div>

        <div class="modal-body position-relative">
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 text-sm text-secondary">Carregando configurações...</p>
          </div>

          <div v-else>
            <ul class="nav nav-tabs mb-3">
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: activeTab === 'config' }" @click="activeTab = 'config'">
                  Configuração
                </button>
              </li>
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: activeTab === 'team' }" @click="activeTab = 'team'">
                  Equipe
                  <span v-if="affiliates.length" class="badge bg-gradient-primary ms-1">{{ affiliates.length }}</span>
                </button>
              </li>
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: activeTab === 'commissions' }" @click="openCommissionsTab">
                  Comissões
                  <span v-if="commissionTotals.pending_count" class="badge bg-gradient-warning ms-1">{{ commissionTotals.pending_count }}</span>
                </button>
              </li>
            </ul>

            <!-- TAB: CONFIG -->
            <div v-show="activeTab === 'config'" class="row">
              <div class="col-12 mb-4">
                <div class="alert bg-gradient-info text-white p-3 border-radius-lg shadow-sm border border-light">
                  <div class="d-flex align-items-center mb-1">
                    <i class="material-icons text-white me-2">group_add</i>
                    <span class="font-weight-bold">Afiliados por convite (privado)</span>
                  </div>
                  <p class="text-xs mb-0 opacity-9">
                    Envie o link de convite só para quem você quiser (ex.: membros da equipe). Cada um ganha um link exclusivo de venda. A rifa <strong>não</strong> aparece para o público em um marketplace.
                  </p>
                </div>
              </div>

              <div class="col-md-6 mb-4">
                <label class="form-label font-weight-bold text-dark">Permitir Afiliações</label>
                <div class="form-check form-switch pe-4 pb-1">
                  <input class="form-check-input" type="checkbox" id="affiliateEnabled" v-model="form.affiliate_enabled">
                  <label class="form-check-label" for="affiliateEnabled">
                    Ativar Programa de Afiliados
                  </label>
                </div>
                <small class="text-xs text-secondary d-block mt-1">Com o programa ativo, quem receber seu link de convite poderá se tornar afiliado desta campanha.</small>
              </div>

              <div class="col-md-6 mb-4">
                <label class="form-label font-weight-bold text-dark">Tipo de Comissão</label>
                <select class="form-control border px-2 py-1" v-model="form.affiliate_commission_type" :disabled="!form.affiliate_enabled">
                  <option value="percentage">Porcentagem (%)</option>
                  <option value="fixed">Valor Fixo (R$)</option>
                </select>
                <small class="text-xs text-secondary mt-1">Defina como recompensar os afiliados.</small>
              </div>

              <div class="col-md-6 mb-4" v-if="form.affiliate_commission_type === 'percentage'">
                <label class="form-label font-weight-bold text-dark">Percentual de Comissão (%)</label>
                <div class="input-group input-group-outline mb-1" :class="{'is-filled': form.affiliate_commission_percentage !== null}">
                  <input type="number" class="form-control" v-model="form.affiliate_commission_percentage" min="0" max="50" step="0.01" :disabled="!form.affiliate_enabled" @input="handlePercentageInput">
                </div>
                <small class="text-xs text-secondary">Apenas números. O limite é 50%.</small>
              </div>

              <div class="col-md-6 mb-4" v-if="form.affiliate_commission_type === 'fixed'">
                <div class="bg-light border-radius-lg p-2 mb-2">
                  <p class="text-xs text-dark mb-1 font-weight-bold">Regra de Segurança:</p>
                  <p class="text-xxs text-secondary mb-0">
                    Cota: <strong>{{ formatCurrency(parseFloat(campaign.quota_price)) }}</strong> <br>
                    Limite (50%): <strong>{{ formatCurrency(maxFixedValueLimit) }}</strong>
                  </p>
                </div>
                <label class="form-label font-weight-bold text-dark">A cada COTA VENDIDA, pagar (R$)</label>
                <div class="input-group input-group-outline mb-1" :class="{'is-filled': display_fixed_value !== ''}">
                  <input type="text" class="form-control" v-model="display_fixed_value" @input="handleFixedValueInput" :disabled="!form.affiliate_enabled" placeholder="R$ 0,00">
                </div>
                <small class="text-xs text-secondary">A comissão vai multiplicar pelas cotas compradas.</small>
              </div>

              <div class="col-md-6 mb-4">
                <label class="form-label font-weight-bold text-dark">Valor mínimo para solicitar PIX (R$)</label>
                <div class="input-group input-group-outline mb-1" :class="{'is-filled': display_min_payout !== ''}">
                  <input type="text" class="form-control" v-model="display_min_payout" @input="handleMinPayoutInput" :disabled="!form.affiliate_enabled" placeholder="R$ 0,00">
                </div>
                <small class="text-xs text-secondary">Quando o afiliado atingir esse valor a receber, o painel dele pede a chave PIX. Use 0 para liberar sempre.</small>
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-label font-weight-bold text-dark">Descrição Curta (Vitrine)</label>
                <div class="input-group input-group-outline mb-1" :class="{'is-filled': true}">
                  <textarea class="form-control" v-model="form.affiliate_description" rows="2" placeholder="Descreva brevemente sua campanha e porque vendê-la vale a pena..." :disabled="!form.affiliate_enabled"></textarea>
                </div>
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-label font-weight-bold text-dark">Termos e Regras Internas</label>
                <div class="input-group input-group-outline mb-1" :class="{'is-filled': true}">
                  <textarea class="form-control" v-model="form.affiliate_terms" rows="3" placeholder="Informações de suporte, regras do produtor, etc." :disabled="!form.affiliate_enabled"></textarea>
                </div>
              </div>

              <div class="col-12 mt-2" v-if="form.affiliate_enabled">
                <label class="form-label font-weight-bold text-dark text-sm w-100 mb-0">Link de convite <small class="fw-normal text-secondary">(envie no grupo / WhatsApp — cada pessoa cria conta e já vira afiliada)</small></label>
                <div class="d-flex w-100 gap-2 mb-2 mt-1">
                  <input type="text" class="form-control border px-2 py-1 bg-light" readonly :value="conviteLink">
                  <button class="btn btn-sm btn-outline-primary mb-0 shadow-none px-3 m-0" @click="copyLink">
                    <i class="material-icons text-sm">content_copy</i>
                  </button>
                </div>
              </div>
            </div>

            <!-- TAB: EQUIPE / RANKING -->
            <div v-show="activeTab === 'team'">
              <div class="row mb-3">
                <div class="col-4">
                  <div class="card p-3 shadow-none border bg-light text-center">
                    <h5 class="mb-0 text-primary">{{ affiliates.length }}</h5>
                    <span class="text-xs text-secondary font-weight-bold">Membros</span>
                  </div>
                </div>
                <div class="col-4">
                  <div class="card p-3 shadow-none border bg-light text-center">
                    <h5 class="mb-0 text-dark">{{ totalQuotasSold }}</h5>
                    <span class="text-xs text-secondary font-weight-bold">Cotas vendidas</span>
                  </div>
                </div>
                <div class="col-4">
                  <div class="card p-3 shadow-none border bg-light text-center">
                    <h5 class="mb-0 text-dark">{{ formatCurrency(totalRevenue) }}</h5>
                    <span class="text-xs text-secondary font-weight-bold">R$ gerado</span>
                  </div>
                </div>
              </div>

              <div v-if="affiliates.length === 0" class="text-center py-4 text-secondary text-sm">
                Ainda não há afiliados. Copie o link de convite na aba Configuração e envie para a equipe.
              </div>
              <div v-else class="table-responsive" style="max-height: 360px; overflow-y: auto;">
                <table class="table table-sm align-middle mb-0">
                  <thead class="bg-light">
                    <tr>
                      <th class="text-xs">#</th>
                      <th class="text-xs">Membro</th>
                      <th class="text-xs">Cliques</th>
                      <th class="text-xs">Cotas</th>
                      <th class="text-xs">Vendido</th>
                      <th class="text-xs">A pagar</th>
                      <th class="text-xs">PIX</th>
                      <th class="text-xs">Status</th>
                      <th class="text-xs"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(aff, idx) in affiliates" :key="aff.id">
                      <td class="text-xs font-weight-bold">{{ idx + 1 }}</td>
                      <td class="text-xs">
                        <strong>{{ (aff.user && aff.user.full_name) || '—' }}</strong>
                        <div class="text-secondary">{{ aff.affiliate_code }}</div>
                      </td>
                      <td class="text-xs">{{ aff.click_count || 0 }}</td>
                      <td class="text-xs">{{ aff.total_quotas || 0 }}</td>
                      <td class="text-xs font-weight-bold">{{ formatCurrency(aff.total_value) }}</td>
                      <td class="text-xs text-warning font-weight-bold">{{ formatCurrency(aff.pending_commission) }}</td>
                      <td class="text-xs">
                        <span v-if="aff.pix_key" class="text-success" :title="`${aff.pix_key_type || ''}: ${aff.pix_key}`">
                          {{ aff.pix_key_type || 'PIX' }} · {{ shortPix(aff.pix_key) }}
                        </span>
                        <span v-else class="text-secondary">—</span>
                      </td>
                      <td class="text-xs">
                        <span class="badge" :class="aff.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                          {{ aff.status === 'active' ? 'Ativo' : 'Pausado' }}
                        </span>
                      </td>
                      <td class="text-end text-nowrap">
                        <button type="button" class="btn btn-sm btn-outline-primary mb-0 me-1 px-2" title="Copiar link de venda" @click="copySalesLink(aff)">
                          <i class="material-icons text-sm">link</i>
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm mb-0 px-2"
                          :class="aff.status === 'active' ? 'btn-outline-secondary' : 'btn-outline-success'"
                          :disabled="updatingStatusId === aff.id"
                          @click="toggleAffiliateStatus(aff)"
                        >
                          {{ aff.status === 'active' ? 'Pausar' : 'Ativar' }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- TAB: COMISSÕES -->
            <div v-show="activeTab === 'commissions'">
              <div class="row mb-3">
                <div class="col-6">
                  <div class="card p-3 shadow-none border bg-light text-center">
                    <h5 class="mb-0 text-warning">{{ formatCurrency(commissionTotals.pending_sum || 0) }}</h5>
                    <span class="text-xs text-secondary font-weight-bold">A pagar ({{ commissionTotals.pending_count || 0 }})</span>
                  </div>
                </div>
                <div class="col-6">
                  <div class="card p-3 shadow-none border bg-light text-center">
                    <h5 class="mb-0 text-success">{{ formatCurrency(commissionTotals.paid_sum || 0) }}</h5>
                    <span class="text-xs text-secondary font-weight-bold">Pago ({{ commissionTotals.paid_count || 0 }})</span>
                  </div>
                </div>
              </div>

              <div v-if="commissionsLoading" class="text-center py-4">
                <div class="spinner-border text-primary spinner-border-sm" role="status"></div>
              </div>
              <div v-else-if="commissions.length === 0" class="text-center py-4 text-secondary text-sm">
                Nenhuma comissão registrada ainda.
              </div>
              <div v-else class="table-responsive" style="max-height: 320px; overflow-y: auto;">
                <table class="table table-sm align-middle mb-0">
                  <thead class="bg-light">
                    <tr>
                      <th class="text-xs">Afiliado</th>
                      <th class="text-xs">Comprador</th>
                      <th class="text-xs">Venda</th>
                      <th class="text-xs">Comissão</th>
                      <th class="text-xs">PIX / Status</th>
                      <th class="text-xs"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in commissions" :key="row.id">
                      <td class="text-xs">
                        <strong>{{ row.affiliate_name || '—' }}</strong>
                        <div class="text-secondary">{{ row.affiliate_code }}</div>
                      </td>
                      <td class="text-xs">{{ row.buyer_name || '—' }}</td>
                      <td class="text-xs">{{ formatCurrency(row.sale_value) }}</td>
                      <td class="text-xs font-weight-bold">{{ formatCurrency(row.commission_value) }}</td>
                      <td class="text-xs">
                        <span class="badge" :class="row.status === 'pending' ? 'bg-gradient-warning' : 'bg-gradient-success'">
                          {{ row.status === 'pending' ? 'A pagar' : 'Pago' }}
                        </span>
                        <div v-if="row.pix_key" class="text-secondary mt-1" :title="row.pix_key">
                          {{ row.pix_key_type || 'PIX' }}: {{ shortPix(row.pix_key) }}
                          <button type="button" class="btn btn-link btn-sm p-0 ms-1 text-xs" @click="copyText(row.pix_key)">copiar</button>
                        </div>
                        <div v-else-if="row.status === 'pending'" class="text-secondary mt-1">Sem chave PIX</div>
                      </td>
                      <td class="text-end">
                        <button
                          v-if="row.status === 'pending'"
                          type="button"
                          class="btn btn-sm bg-gradient-success mb-0"
                          :disabled="markingPaidId === row.id"
                          @click="markPaid(row)"
                        >
                          <span v-if="markingPaidId === row.id" class="spinner-border spinner-border-sm"></span>
                          <span v-else>Marcar pago</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn bg-gradient-secondary" @click="closeModal">Fechar</button>
          <button
            v-if="activeTab === 'config'"
            type="button"
            class="btn bg-gradient-primary"
            @click="save"
            :disabled="loading || updating"
          >
            <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="updating"></span>
            Salvar Configurações
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import AffiliateHttp from "@/http/AffiliateHttp";
import { buildCampaignPublicUrl } from "@/store/config.js";

export default {
  name: "AffiliateConfigModal",
  emits: ["close", "updated"],
  props: {
    campaignProp: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      modalInstance: null,
      loading: true,
      updating: false,
      activeTab: "config",
      currentUser: {},
      campaign: {},
      affiliates: [],
      materials: [],
      commissions: [],
      commissionTotals: {
        pending_count: 0,
        pending_sum: 0,
        paid_count: 0,
        paid_sum: 0,
      },
      commissionsLoading: false,
      commissionsLoaded: false,
      markingPaidId: null,
      updatingStatusId: null,
      alert: {
        show: false,
        type: 'success',
        message: ''
      },
      display_fixed_value: '',
      display_min_payout: '',
      form: {
        id: null,
        affiliate_enabled: false,
        affiliate_commission_type: 'percentage',
        affiliate_commission_percentage: 0,
        affiliate_commission_fixed_value: 0,
        affiliate_min_payout: 0,
        affiliate_description: '',
        affiliate_terms: ''
      }
    };
  },
  computed: {
    conviteLink() {
      return `${window.location.origin}/sign-in?affiliate_campaign=${this.campaign.id}`;
    },
    totalSales() {
      if(!this.affiliates) return 0;
      return this.affiliates.reduce((acc, curr) => acc + (curr.total_sales || 0), 0);
    },
    totalQuotasSold() {
      if(!this.affiliates) return 0;
      return this.affiliates.reduce((acc, curr) => acc + (curr.total_quotas || 0), 0);
    },
    totalRevenue() {
      if(!this.affiliates) return 0;
      return this.affiliates.reduce((acc, curr) => acc + (curr.total_value || 0), 0);
    },
    maxFixedValueLimit() {
      if (!this.campaign.quota_price) return 0;
      return parseFloat(this.campaign.quota_price) * 0.5;
    }
  },
  mounted() {
    this.currentUser = this.$store.getters.user;
    this.modalInstance = new Modal(this.$refs.modal, {
      backdrop: "static",
      keyboard: false,
    });
    this.fetchData();
    this.modalInstance.show();
  },
  methods: {
    showAlert(type, message) {
      this.alert = { show: true, type, message };
      if (type === 'success') {
        setTimeout(() => { this.alert.show = false; }, 3000);
      }
    },
    formatDateCurrency(val) {
      if(!val) return "0,00";
      return val.toLocaleString('pt-BR', {minimumFractionDigits: 2});
    },
    async fetchData() {
      this.loading = true;
      try {
        const res = await AffiliateHttp.getAffiliateConfig(this.campaignProp.id);
        const data = res.data;
        this.campaign = data.campaign || this.campaignProp;
        this.affiliates = data.affiliates || [];
        this.materials = data.materials || [];
        
        this.form = {
          id: this.campaign.id,
          affiliate_enabled: this.campaign.affiliate_enabled,
          affiliate_commission_type: this.campaign.affiliate_commission_type || 'percentage',
          affiliate_commission_percentage: this.campaign.affiliate_commission_percentage || 0,
          affiliate_commission_fixed_value: this.campaign.affiliate_commission_fixed_value || 0,
          affiliate_min_payout: this.campaign.affiliate_min_payout || 0,
          affiliate_description: this.campaign.affiliate_description || '',
          affiliate_terms: this.campaign.affiliate_terms || ''
        };
        this.display_fixed_value = this.maskCurrency(this.form.affiliate_commission_fixed_value);
        this.display_min_payout = this.maskCurrency(this.form.affiliate_min_payout);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async openCommissionsTab() {
      this.activeTab = 'commissions';
      if (!this.commissionsLoaded) {
        await this.fetchCommissions();
      }
    },
    async fetchCommissions() {
      this.commissionsLoading = true;
      try {
        const res = await AffiliateHttp.getCampaignCommissions(this.campaignProp.id);
        this.commissions = res.data.commissions || [];
        this.commissionTotals = res.data.totals || {
          pending_count: 0,
          pending_sum: 0,
          paid_count: 0,
          paid_sum: 0,
        };
        this.commissionsLoaded = true;
      } catch (e) {
        console.error(e);
        this.showAlert('error', 'Não foi possível carregar as comissões.');
      } finally {
        this.commissionsLoading = false;
      }
    },
    async markPaid(row) {
      let hint = 'Nota opcional do pagamento (ex: PIX enviado em 16/07):';
      if (row.pix_key) {
        hint = `PIX do afiliado (${row.pix_key_type || 'chave'}): ${row.pix_key}\n\nNota opcional do pagamento:`;
      }
      const note = window.prompt(hint, '') || null;
      this.markingPaidId = row.id;
      try {
        await AffiliateHttp.markCommissionPaid(row.id, note);
        this.showAlert('success', 'Comissão marcada como paga.');
        this.commissionsLoaded = false;
        await this.fetchCommissions();
        await this.fetchData();
      } catch (e) {
        let msg = 'Não foi possível marcar como pago.';
        if (e.response && e.response.data && e.response.data.error) msg = e.response.data.error;
        this.showAlert('error', msg);
      } finally {
        this.markingPaidId = null;
      }
    },
    shortPix(key) {
      if (!key) return '—';
      if (key.length <= 18) return key;
      return `${key.slice(0, 8)}…${key.slice(-6)}`;
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.showAlert('success', 'Chave PIX copiada.');
      } catch (e) {
        this.showAlert('error', 'Não foi possível copiar.');
      }
    },
    async save() {
      this.alert.show = false;

      if(this.form.affiliate_enabled) {
        if(this.form.affiliate_commission_type === 'percentage') {
          if(this.form.affiliate_commission_percentage < 0 || this.form.affiliate_commission_percentage > 50) {
            return this.showAlert('error', 'Porcentagem deve estar entre 0 e 50%.');
          }
        } else {
          if(this.form.affiliate_commission_fixed_value < 0) {
            return this.showAlert('error', 'Valor fixo inválido.');
          }
          if(this.form.affiliate_commission_fixed_value > this.maxFixedValueLimit) {
            return this.showAlert('error', `O valor fixo não pode exceder 50% da cota (${this.formatCurrency(this.maxFixedValueLimit)}).`);
          }
        }
      }

      this.updating = true;
      try {
        await AffiliateHttp.updateAffiliateConfig(this.form);
        this.showAlert('success', 'Configurações salvas com sucesso!');
        this.$emit("updated");
      } catch (e) {
        let msg = "Não foi possível salvar.";
        if(e.response && e.response.data && e.response.data.error) msg = e.response.data.error;
        this.showAlert('error', msg);
      } finally {
        this.updating = false;
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.conviteLink);
        this.showAlert('success', 'Link de convite copiado com sucesso!');
      } catch (error) {
        this.showAlert('error', 'Não foi possível copiar o link.');
      }
    },
    async copySalesLink(aff) {
      const url = `${buildCampaignPublicUrl(this.campaign.slug, this.currentUser)}?ref=${aff.affiliate_code}`;
      try {
        await navigator.clipboard.writeText(url);
        this.showAlert('success', `Link de venda de ${(aff.user && aff.user.full_name) || aff.affiliate_code} copiado.`);
      } catch (e) {
        this.showAlert('error', 'Não foi possível copiar o link.');
      }
    },
    async toggleAffiliateStatus(aff) {
      const next = aff.status === 'active' ? 'inactive' : 'active';
      this.updatingStatusId = aff.id;
      try {
        await AffiliateHttp.updateAffiliateStatus(aff.id, next);
        aff.status = next;
        this.showAlert('success', next === 'active' ? 'Afiliado reativado.' : 'Afiliado pausado.');
      } catch (e) {
        let msg = 'Não foi possível atualizar o status.';
        if (e.response && e.response.data && e.response.data.error) msg = e.response.data.error;
        this.showAlert('error', msg);
      } finally {
        this.updatingStatusId = null;
      }
    },
    closeModal() {
      this.modalInstance.hide();
      this.$emit("close");
    },
    handlePercentageInput() {
      if (this.form.affiliate_commission_percentage > 50) {
        this.form.affiliate_commission_percentage = 50;
      }
    },
    handleFixedValueInput(e) {
      let val = e.target.value.replace(/\D/g, "");
      let floatVal = parseFloat(val) / 100;
      
      if (floatVal > this.maxFixedValueLimit) {
        floatVal = this.maxFixedValueLimit;
      }
      
      this.form.affiliate_commission_fixed_value = floatVal;
      this.display_fixed_value = this.maskCurrency(floatVal);
    },
    handleMinPayoutInput(e) {
      let val = e.target.value.replace(/\D/g, "");
      let floatVal = parseFloat(val) / 100;
      if (isNaN(floatVal)) floatVal = 0;
      this.form.affiliate_min_payout = floatVal;
      this.display_min_payout = this.maskCurrency(floatVal);
    },
    maskCurrency(val) {
      if (!val && val !== 0) return "";
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(val);
    },
    formatCurrency(val) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(val || 0);
    }
  },
};
</script>

<style scoped>
.modal-header .btn-close {
  color: white;
  background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") center/1em auto no-repeat;
}
.nav-tabs .nav-link {
  border: none;
  color: #67748e;
  font-weight: 600;
  font-size: 0.85rem;
  background: transparent;
}
.nav-tabs .nav-link.active {
  color: #344767;
  border-bottom: 2px solid #5e72e4;
}
</style>
